import { createSystem, defaultConfig, defineRecipe } from '@chakra-ui/react';
import '@fontsource-variable/dm-sans';
import '@fontsource/inter';

const colors = {
  black: { value: '#011213' },
  primary: {
    50: '#f2fbf9',
    100: '#d2f5eb',
    200: '#a5eada',
    300: '#70d8c2',
    400: '#43beaa',
    500: '#269584',
    600: '#1f8275',
    700: '#1c6960',
    800: '#1b544e',
    900: '#1b4641',
    950: '#0a2928',
  },
  grey: {
    50: '#f6f7f7',
    100: '#e2e5e4',
    200: '#c4cbc9',
    300: '#9eaaa6',
    400: '#8c9894',
    500: '#5f6d68',
    600: '#4b5653',
    700: '#3e4744',
    800: '#343b39',
    900: '#2e3332',
    950: '#181b1a',
  },
  red: {
    50: '#fdf3f3',
    100: '#fce4e4',
    200: '#facece',
    300: '#f5acac',
    400: '#ee7b7b',
    500: '#e25151',
    600: '#ce3434',
    700: '#ad2828',
    800: '#952626',
    900: '#782424',
    950: '#410e0e',
  },
  pink: {
    50: '#fcf3f9',
    100: '#fae9f5',
    200: '#f7d3eb',
    300: '#f1b0da',
    400: '#e77fc0',
    500: '#db59a6',
    600: '#c93988',
    700: '#ad296d',
    800: '#95265e',
    900: '#78234e',
    950: '#490e2c',
  },
  purple: {
    50: '#faf6fe',
    100: '#f2e9fe',
    200: '#e7d7fd',
    300: '#d5b8fa',
    400: '#ba8bf5',
    500: '#a05eee',
    600: '#8a3edf',
    700: '#752cc4',
    800: '#5d2695',
    900: '#522281',
    950: '#360c5f',
  },
  blue: {
    50: '#f3f7fc',
    100: '#e6eef8',
    200: '#c7dbf0',
    300: '#96bde3',
    400: '#5e9bd2',
    500: '#397fbe',
    600: '#265d95',
    700: '#225082',
    800: '#20466c',
    900: '#1f3b5b',
    950: '#15263c',
  },
  cyan: {
    50: '#f2fbfa',
    100: '#d2f5f1',
    200: '#a5eae5',
    300: '#70d8d2',
    400: '#43bebd',
    500: '#269595',
    600: '#1f8082',
    700: '#1c6769',
    800: '#1b5254',
    900: '#1b4446',
    950: '#0a2629',
  },
  teal: {
    50: '#eefbf5',
    100: '#d7f4e5',
    200: '#b2e8cf',
    300: '#80d5b3',
    400: '#4bbc93',
    500: '#269570',
    600: '#1a8161',
    700: '#156750',
    800: '#135240',
    900: '#114336',
    950: '#08261f',
  },
  green: {
    50: '#f1fcf3',
    100: '#e0f8e3',
    200: '#c2f0c9',
    300: '#92e39f',
    400: '#5ace6d',
    500: '#34b34a',
    600: '#269539',
    700: '#217430',
    800: '#1f5c2a',
    900: '#1b4c24',
    950: '#0a2911',
  },
  yellow: {
    50: '#f9faec',
    100: '#f0f3ce',
    200: '#e6e8a0',
    300: '#dad96a',
    400: '#cec741',
    500: '#bfb233',
    600: '#958226',
    700: '#846c24',
    800: '#6e5725',
    900: '#5f4924',
    950: '#372811',
  },
  orange: {
    50: '#faf7ec',
    100: '#f3eace',
    200: '#e8d3a0',
    300: '#dab56a',
    400: '#ce9a41',
    500: '#bf8533',
    600: '#955e26',
    700: '#844c24',
    800: '#6e3f25',
    900: '#5f3624',
    950: '#371b11',
  },
};

const buttonRecipe = defineRecipe({
  base: {
    _hover: {
      color: 'white',
      backgroundColor: 'primary.500 !important',
      borderColor: 'primary.500 !important',
    },
  },
  variants: {
    variant: {
      outline: {
        borderColor: { _dark: 'gray.500' },
      },
    },
  },
});

export const system = createSystem(defaultConfig, {
  cssVarsPrefix: 'r3',
  strictTokens: true,
  globalCss: {
    'html, body': {
      backgroundColor: { base: 'gray.50', _dark: 'gray.900' },
      color: { base: 'primary.900', _dark: 'primary.50' },
    },
    em: {
      fontStyle: 'italic',
    },
    '&::-webkit-scrollbar': {
      width: '8px',
      background: 'primary.100',
    },
    '&::-webkit-scrollbar:horizontal': {
      height: '8px',
      background: 'primary.100',
    },
    '&::-webkit-scrollbar-track': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'primary.500',
    },
    '.ql-editor': {
      fontFamily: 'var(--r3-fonts-body)',
      color: 'var(--r3-colors-gray-800)',
      minHeight: '150px',
    },
    '.ql-toolbar': {
      border: '1px solid var(--r3-colors-border) !important',
      borderBottom: 'none !important',
      borderRadius: 'var(--r3-radii-l2) var(--r3-radii-l2) 0 0',
    },
    '.ql-container': {
      border: '1px solid var(--r3-colors-border) !important',
      borderRadius: '0 0 var(--r3-radii-l2) var(--r3-radii-l2)',
    },
    '.ql-container p': {
      display: 'block',
    },
    '.ql-viewer': {
      minHeight: 'unset',
      padding: '0 !important',
    },
  },
  theme: {
    keyframes: {
      pulse: {
        '0%, 100%': { opacity: 0.8 },
        '50%': { opacity: 1 },
      },
    },
    tokens: {
      animations: {
        pulse: { value: 'pulse 2s infinite' },
      },
      colors: { ...colors },
      fonts: {
        heading: { value: "'DM Sans Variable', sans-serif" },
        body: { value: "'DM Sans Variable', sans-serif" },
        mono: { value: "'DM Sans Variable', sans-serif" },
      },
      cursor: {
        option: { value: 'pointer' },
        checkbox: { value: 'pointer' },
        menuitem: { value: 'pointer' },
        radio: { value: 'pointer' },
        slider: { value: 'pointer' },
        switch: { value: 'pointer' },
      },
    },
    semanticTokens: {
      colors: {
        border: {
          DEFAULT: {
            value: { base: '{colors.gray.200}', _dark: '{colors.gray.500}' },
          },
          solid: {
            value: { base: '{colors.gray.200}', _dark: '{colors.gray.500}' },
          },
          contrast: {
            value: { base: '{colors.gray.100}', _dark: '{colors.gray.900}' },
          },
          fg: {
            value: { base: '{colors.gray.700}', _dark: '{colors.gray.300}' },
          },
          muted: {
            value: { base: '{colors.gray.100}', _dark: '{colors.gray.900}' },
          },
          subtle: {
            value: { base: '{colors.gray.200}', _dark: '{colors.gray.800}' },
          },
          emphasized: {
            value: { base: '{colors.gray.300}', _dark: '{colors.gray.50}' },
          },
          focusRing: {
            value: { base: '{colors.gray.200}', _dark: '{colors.gray.500}' },
          },
        },
        primary: {
          DEFAULT: { value: '{colors.primary.500}' },
          solid: { value: '{colors.primary.500}' },
          contrast: {
            value: {
              base: '{colors.primary.100}',
              _dark: '{colors.primary.900}',
            },
          },
          fg: {
            value: {
              base: '{colors.primary.700}',
              _dark: '{colors.primary.300}',
            },
          },
          muted: {
            value: {
              base: '{colors.primary.100}',
              _dark: '{colors.primary.900}',
            },
          },
          subtle: {
            value: {
              base: '{colors.primary.200}',
              _dark: '{colors.primary.800}',
            },
          },
          emphasized: {
            value: {
              base: '{colors.primary.300}',
              _dark: '{colors.primary.700}',
            },
          },
          focusRing: { value: '{colors.primary.500}' },
        },
        secondary: {
          DEFAULT: { value: '{colors.secondary.500}' },
          solid: { value: '{colors.secondary.500}' },
          contrast: {
            value: {
              base: '{colors.secondary.100}',
              _dark: '{colors.secondary.900}',
            },
          },
          fg: {
            value: {
              base: '{colors.secondary.700}',
              _dark: '{colors.secondary.300}',
            },
          },
          muted: {
            value: {
              base: '{colors.secondary.100}',
              _dark: '{colors.secondary.900}',
            },
          },
          subtle: {
            value: {
              base: '{colors.secondary.200}',
              _dark: '{colors.secondary.800}',
            },
          },
          emphasized: {
            value: {
              base: '{colors.secondary.300}',
              _dark: '{colors.secondary.700}',
            },
          },
          focusRing: { value: '{colors.secondary.500}' },
        },
      },
    },
    recipes: {
      button: buttonRecipe,
    },
  },
});

export default system;
